import { useMemo } from 'react';
import { ButtonStatus } from '~components/navigation-panel/status-button';
import {
  AmendmentDetailOption,
  AmendmentDetailStatusOption,
  BookingFormStatusOption,
  BookingLinkStatusTypeOption,
  BookingStatusOption,
  DocumentSettingStageOption,
  DocumentStatusOption,
  FormSettingTypeOption,
  PaymentItemStatusOptions,
} from '~graphql/__generated__/globalTypes';
import {
  SP_MyPaymentGetBookingInfo_studentGetBooking,
  SP_MyPaymentGetBookingInfo_studentGetBooking_paymentItems,
} from '~graphql/__generated__/SP_MyPaymentGetBookingInfo';
import {
  SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings,
  SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings_bookingForms,
  SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings_paymentItems,
} from '~graphql/__generated__/SP_QueryStudentGetBookings';
import { SP_StudentGetBookingInfo_studentGetBooking } from '~graphql/__generated__/SP_StudentGetBookingInfo';
import { useTranslation } from '~i18n';
import { notEmpty } from '~utils/js-helper';
import { useIsExtensionBooking } from '~utils/use-amendment';

export enum BookingType {
  CONFIRM = 'CONFIRM',
  AMENDMENT = 'AMENDMENT',
  PAYMENT = 'PAYMENT',
  EXTENSION = 'EXTENSION',
  TAKEOVER = 'TAKEOVER',
}

type bookingType =
  | SP_StudentGetBookingInfo_studentGetBooking
  | SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings
  | null
  | undefined;

type fullBookingType =
  | SP_StudentGetBookingInfo_studentGetBooking
  | SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings
  | SP_MyPaymentGetBookingInfo_studentGetBooking
  | null
  | undefined;

export const useGetBookingCardType = (booking: bookingType) => {
  const { status, latestAmendmentDetail, bookingLink } = booking || {};
  const { type } = latestAmendmentDetail || {};

  return useMemo(() => {
    if (bookingLink && bookingLink?.status === BookingLinkStatusTypeOption.PENDING) {
      return BookingType.TAKEOVER;
    }

    if (latestAmendmentDetail?.status === AmendmentDetailStatusOption.PENDING) {
      if (
        type === AmendmentDetailOption.TENANCY_EXTEND_EARLY_CHECK_IN ||
        type === AmendmentDetailOption.TENANCY_EXTEND_LATER_CHECK_OUT
      ) {
        return BookingType.EXTENSION;
      }

      return BookingType.AMENDMENT;
    } else {
      if (
        status &&
        [BookingStatusOption.BOOKING_COMPLETED, BookingStatusOption.BOOKING_CONFIRMED].includes(
          status
        )
      )
        return BookingType.CONFIRM;
      return BookingType.PAYMENT;
    }
  }, [bookingLink, latestAmendmentDetail, status, type]);
};

export const useGetBookingCardName = (booking: bookingType) => {
  const { t } = useTranslation(['student_portal']);
  const { roomAllocation, property } = booking || {};

  return useMemo(() => {
    return [
      roomAllocation?.bedName,
      roomAllocation?.buildingName,
      t('property_name_in_city', {
        property: property?.name,
        city: property?.city?.name,
      }),
    ]
      .filter(notEmpty)
      .join(', ');
  }, [property, roomAllocation, t]);
};

export const useGetBookingCardStatus = (
  booking: bookingType
): {
  name: string;
  status: ButtonStatus;
} => {
  const { t } = useTranslation(['student_portal']);
  const { status } = booking || {};
  const bookingCardType = useGetBookingCardType(booking);

  return useMemo(() => {
    if (status && [BookingStatusOption.PAST, BookingStatusOption.CANCELLED].includes(status)) {
      return {
        name: t(`my_stay.status.${status.toLocaleLowerCase()}`),
        status: 'error',
      };
    }

    if (bookingCardType === BookingType.AMENDMENT) {
      return {
        name: t('amendment.pending'),
        status: 'warning',
      };
    }

    if (bookingCardType === BookingType.TAKEOVER) {
      return {
        name: t('takeover.pending'),
        status: 'warning',
      };
    }

    return {
      name: t(`my_stay.status.${status?.toLocaleLowerCase()}`),
      status: 'complete',
    };
  }, [bookingCardType, status, t]);
};

export const useDisplayArrivalSlot = (booking: fullBookingType) => {
  const { latestAmendmentDetail, checkInTaskStarted } = booking || {};
  const isExtensionBooking = useIsExtensionBooking(
    latestAmendmentDetail?.status,
    latestAmendmentDetail?.type
  );

  return useMemo(() => {
    if (isExtensionBooking) return false;
    return !!checkInTaskStarted;
  }, [checkInTaskStarted, isExtensionBooking]);
};

export const useBookingDocumentCompleted = (booking: fullBookingType) => {
  const { documents } = booking || {};

  return useMemo(() => {
    if (documents) {
      return documents.every(item => {
        return item?.mandatory && item?.status === DocumentStatusOption.CONFIRMED;
      });
    }
    return true;
  }, [documents]);
};

export const useGetBookingPaymentItems = (booking: fullBookingType) => {
  const { tenancyOption, paymentItems } = booking || {};

  return useMemo(() => {
    return (
      paymentItems?.filter(item => {
        return (
          !item?.isDueBeforeBookingConfirmed &&
          new Date(item?.dueDatetime).getTime() < new Date(tenancyOption?.startDate).getTime()
        );
      }) || []
    );
  }, [paymentItems, tenancyOption]);
};

export const useBookingPaymentItemsIsPaid = (
  paymentItems: (
    | SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings_paymentItems
    | SP_MyPaymentGetBookingInfo_studentGetBooking_paymentItems
    | null
  )[]
) => {
  return useMemo(() => {
    return paymentItems.every((item: any) => {
      return item?.status === PaymentItemStatusOptions.PAID;
    });
  }, [paymentItems]);
};

export const useGetBookingReviewForm = (booking: fullBookingType) => {
  const { bookingForms } = booking || {};

  return useMemo(() => {
    return (
      bookingForms?.filter(i => {
        return (
          i?.type === FormSettingTypeOption.ACKNOWLEDGE ||
          i?.type === FormSettingTypeOption.ACCEPT_OR_REJECT
        );
      }) || []
    );
  }, [bookingForms]);
};

export const useBookingReviewFormsInComplete = (
  forms: (SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings_bookingForms | null)[]
) => {
  return useMemo(() => {
    return forms.some(i => i?.status === BookingFormStatusOption.INCOMPLETE);
  }, [forms]);
};

export const useGetBookingTaskCompletedStatus = (
  booking: fullBookingType,
  contact: any
): {
  task: Number;
  completed: Number;
} => {
  const { arrivalInformation, documents } = booking || {};

  const isDisplayArrivalSlot = useDisplayArrivalSlot(booking);

  const isDocumentCompleted = useBookingDocumentCompleted(booking);

  const bookingPaymentItemsNeedToPay = useGetBookingPaymentItems(booking);
  const isPaymentItemAllPaid = useBookingPaymentItemsIsPaid(bookingPaymentItemsNeedToPay);

  const bookingReviewForms = useGetBookingReviewForm(booking);
  const isBookingReviewFormInComplete = useBookingReviewFormsInComplete(bookingReviewForms);

  return useMemo(() => {
    const completedStatus = {
      task: 1,
      completed: 0,
    };

    if (isDisplayArrivalSlot) {
      completedStatus.task += 1;
      if (arrivalInformation) completedStatus.completed += 1;
    }

    if (contact?.emergencyContacts) completedStatus.completed += 1;

    if (documents && documents?.length > 0) {
      completedStatus.task += 1;
      if (isDocumentCompleted) {
        completedStatus.completed += 1;
      }
    }
    if (bookingPaymentItemsNeedToPay?.length > 0) {
      completedStatus.task += 1;
      if (isPaymentItemAllPaid) {
        completedStatus.completed += 1;
      }
    }
    if (bookingReviewForms.length > 0) {
      completedStatus.task += 1;
      if (!isBookingReviewFormInComplete) {
        completedStatus.completed += 1;
      }
    }

    return completedStatus;
  }, [
    arrivalInformation,
    bookingPaymentItemsNeedToPay,
    bookingReviewForms.length,
    contact,
    documents,
    isBookingReviewFormInComplete,
    isDisplayArrivalSlot,
    isDocumentCompleted,
    isPaymentItemAllPaid,
  ]);
};
